import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Crown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const LABELS = {
  deposit: { title: "Reserve with a deposit", price: "$150", note: "Secures your seat. Balance due before class begins." },
  full: { title: "Pay in full", price: "", note: "Lock in your spot and you're all set — nothing more to pay." },
};

export const PaymentDialog = ({ open, onOpenChange, pkg, fullPrice }) => {
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const info = LABELS[pkg] || LABELS.deposit;
  const priceText = pkg === "full" ? fullPrice : info.price;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pay = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package: pkg,
        origin_url: window.location.origin,
        name: form.name,
        email: form.email,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-wine-deep border border-gold/25 text-cream" data-testid="payment-dialog">
        <DialogHeader>
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gold mb-2">
            <Crown className="h-5 w-5 text-wine-deep" />
          </div>
          <DialogTitle className="font-display text-3xl font-light">
            {info.title} <span className="text-gold">· {priceText}</span>
          </DialogTitle>
          <DialogDescription className="text-cream/60">
            Crowned™ Pro — Level 1 · {info.note}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={pay} className="space-y-4 mt-2">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/50">Full Name</label>
            <input
              data-testid="pay-name"
              value={form.name}
              onChange={update("name")}
              placeholder="Your name"
              className="mt-2 w-full rounded-xl bg-cream/5 border border-cream/15 px-4 py-3.5 text-cream placeholder-cream/30 outline-none focus:border-gold transition-colors duration-300"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/50">Email</label>
            <input
              data-testid="pay-email"
              type="email"
              value={form.email}
              onChange={update("email")}
              placeholder="you@email.com"
              className="mt-2 w-full rounded-xl bg-cream/5 border border-cream/15 px-4 py-3.5 text-cream placeholder-cream/30 outline-none focus:border-gold transition-colors duration-300"
            />
          </div>
          <motion.button
            type="submit"
            data-testid="pay-submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 font-mono text-[12px] uppercase tracking-[0.18em] text-wine-deep hover:bg-cream transition-colors duration-300 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : `Continue to secure checkout · ${priceText}`}
          </motion.button>
          <p className="text-center text-cream/40 text-xs">
            Payments processed securely by Stripe.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};
